// =====================================================
// SITE CATALOG PAGE - Catálogo del Centro
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Settings2, FolderTree, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { catalogApi } from '../../api/catalog';
import { CatalogTree } from '../../components/catalog';
import { Spinner } from '../../components/ui/Spinner';
import { toast } from '../../components/ui/Toast';
import type { SiteCatalogResponse } from '../../types/catalog';

export function SiteCatalogPage() {
  const { activeSite } = useAuth();
  const [catalog, setCatalog] = useState<SiteCatalogResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar catálogo
  const loadCatalog = useCallback(async (showRefresh = false) => {
    if (!activeSite) return;

    if (showRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const data = await catalogApi.getSiteCatalog(activeSite.id);
      setCatalog(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar el catálogo');
      toast.error('Error', err.message || 'No se pudo cargar el catálogo');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeSite]);

  // Cargar al montar o cambiar de sede
  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // Refrescar
  const handleRefresh = () => {
    loadCatalog(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Spinner size="lg" />
        <p className="mt-4 text-sm text-gray-500">Cargando catálogo...</p>
      </div>
    );
  }

  // Error state
  if (error && !catalog) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <XCircle className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Error al cargar</h2>
        <p className="text-gray-500 mb-4">{error}</p>
        <button
          onClick={() => loadCatalog()}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!catalog) return null;

  // Calcular porcentajes
  const specialtyPercent = catalog.summary.totalSpecialties > 0
    ? Math.round((catalog.summary.linkedSpecialties / catalog.summary.totalSpecialties) * 100)
    : 0;
  const actPercent = catalog.summary.totalActs > 0
    ? Math.round((catalog.summary.linkedActs / catalog.summary.totalActs) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <FolderTree className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Catálogo del Centro</h1>
            <p className="text-sm text-gray-500">
              Gestiona los servicios disponibles en <span className="font-medium">{catalog.siteName}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Especialidades */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Especialidades</span>
            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
              {specialtyPercent}%
            </span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-gray-900">
              {catalog.summary.linkedSpecialties}
            </span>
            <span className="text-sm text-gray-400">
              / {catalog.summary.totalSpecialties}
            </span>
          </div>
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${specialtyPercent}%` }}
            />
          </div>
        </div>

        {/* Subespecialidades */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Subespecialidades</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-gray-900">
              {catalog.summary.linkedSubspecialties}
            </span>
            <span className="text-sm text-gray-400">
              / {catalog.summary.totalSubspecialties}
            </span>
          </div>
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500 rounded-full transition-all duration-500"
              style={{
                width: `${
                  catalog.summary.totalSubspecialties > 0
                    ? (catalog.summary.linkedSubspecialties / catalog.summary.totalSubspecialties) * 100
                    : 0
                }%`,
              }}
            />
          </div>
        </div>

        {/* Grupos */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Grupos</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-gray-900">
              {catalog.summary.linkedGroups}
            </span>
            <span className="text-sm text-gray-400">
              / {catalog.summary.totalGroups}
            </span>
          </div>
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 rounded-full transition-all duration-500"
              style={{
                width: `${
                  catalog.summary.totalGroups > 0
                    ? (catalog.summary.linkedGroups / catalog.summary.totalGroups) * 100
                    : 0
                }%`,
              }}
            />
          </div>
        </div>

        {/* Actos */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Actos médicos</span>
            <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              {actPercent}%
            </span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-gray-900">
              {catalog.summary.linkedActs}
            </span>
            <span className="text-sm text-gray-400">
              / {catalog.summary.totalActs}
            </span>
          </div>
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-500"
              style={{ width: `${actPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Config info */}
      {(catalog.defaultSocietyName || catalog.agreementPercent > 0) && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Settings2 className="h-4 w-4" />
            <span className="font-medium">Configuración del centro:</span>
            {catalog.defaultSocietyName && (
              <span>
                Sociedad por defecto: <strong>{catalog.defaultSocietyName}</strong>
              </span>
            )}
            {catalog.agreementPercent > 0 && (
              <span>
                • % Acuerdo: <strong>{catalog.agreementPercent}%</strong>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Catalog Tree */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <CatalogTree
          siteId={activeSite?.id || ''}
          specialties={catalog.specialties}
          onRefresh={handleRefresh}
        />
      </div>

      {/* Help text */}
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex gap-3">
          <CheckCircle2 className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-700 mb-1">¿Cómo funciona?</p>
            <ul className="space-y-1 text-gray-500">
              <li>• <strong>Vincular un hijo</strong> vincula automáticamente a sus padres</li>
              <li>• <strong>Desvincular un padre</strong> desvincula automáticamente a todos sus hijos</li>
              <li>• Los elementos vinculados estarán disponibles para crear presupuestos</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SiteCatalogPage;
